'use client';
import React, { useEffect, useState } from 'react';
import { Box } from '@radix-ui/themes';
import { RenderFields } from './RenderFields';
import { ApiAttribute } from '@context/openapi/types';
import { isFieldVisibilityOverridden } from '@/services/util';
import InputLabel from '@components/ui/DynamicForm/InputLabel';
import { ARRAY_ITEM_NAME } from '@/constant/Constants';
import { useExplorer } from '@/hooks/useExplorer';

interface ArrayInputProps {
  field: ApiAttribute;
  childFieldNameOverride?: string;
  onChange: (field: ApiAttribute, value: string) => void;
  onFilterChange?: (field: ApiAttribute, value: string) => void;
  updateItem?: (field: ApiAttribute) => void;
  isEmptyAttributesVisible: boolean;
}

export const ArrayInput: React.FC<ArrayInputProps> = ({
  field,
  onChange,
  onFilterChange,
  childFieldNameOverride,
  isEmptyAttributesVisible,
  updateItem,
}) => {
  const { visibleFieldsOverride, endpointAttributes } = useExplorer();
  const childItemLength = field.defaultArrayElement
    ? field.child.length / field.defaultArrayElement.length
    : 0;

  const [_isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isEmptyAttributesVisible) {
      let currentIndex = childItemLength - 1;
      while (currentIndex > 0) {
        const hasValue = field.child.some(
          (child) => child.arrayIndex === currentIndex && child.value
        );
        if (!hasValue) {
          removeItem(currentIndex);
        }
        currentIndex -= 1;
      }
    }
  }, [endpointAttributes, childItemLength]);

  const removeItem = (index: number, event?: React.MouseEvent) => {
    event?.stopPropagation();

    field.child = field.child?.map((child) => {
      if (child.arrayIndex === index) {
        return { ...child, arrayIndex: -1 };
      } else if (child.arrayIndex! > index) {
        return { ...child, arrayIndex: child.arrayIndex! - 1 };
      }
      return child;
    });
    field.child = field.child?.filter((child) => child.arrayIndex !== -1);

    if (updateItem) {
      updateItem(field);
    }
  };

  const onAddArrayItem = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!field.child) {
      field.child = field.defaultArrayElement ?? [];
    }
    const newItem: ApiAttribute[] =
      field.defaultArrayElement?.map((child) => {
        return { ...child, arrayIndex: childItemLength, value: '' };
      }) ?? [];
    newItem?.forEach((child) => {
      field.child.push(child);
    });
    if (updateItem) {
      updateItem(field);
    }
  };

  const handleBoxClick = (event: React.MouseEvent) => {
    if (!childItemLength) onAddArrayItem(event);
  };

  const childByArrayIndex = (index: number) => {
    return field.child?.filter((child) => child.arrayIndex === index) ?? [];
  };

  const handleFocus = (hovered: boolean) => {
    setIsFocused(hovered);
  };

  return (
    <Box
      className={`
        text-[#012a38] w-full px-2 bg-[#d3e4e6]
        border-b border-solid border-form-border
        border-l border-r
      `}
      onClick={handleBoxClick}
    >
      <div key={field.key}>
        <div className="flex gap-2 group">
          <InputLabel field={field} />
          <div>
            <button
              type="button"
              onClick={onAddArrayItem}
              onFocus={() => handleFocus(true)}
              onBlur={() => handleFocus(false)}
              className={`ml-auto flex gap-2 py-1 text-xs opacity-100 focus-visible:outline-shade-aqua-300 focus-visible:px-2`}
            >
              + Add item
            </button>
          </div>
        </div>
        <div>
          {Array.from({
            length: childItemLength,
          }).map((_, index) => (
            <div
              key={`array-item-${index}`}
              className="w-full p-4 flex flex-col relative border rounded-lg bg-[#f7fbfb] my-1 pt-8"
            >
              <button
                type="button"
                onClick={(e) => removeItem(index, e)}
                className="absolute top-2 right-2 text-black-600 hover:text-black-800 px-2 hover:bg-[#e0ebec] rounded-lg focus-visible:outline-shade-aqua-300"
              >
                ✕
              </button>
              <div className="w-[100%] mx-auto [&>*:first-child]:border-t [&>*:first-child]:border-solid [&>*:first-child]:border-form-border">
                {childByArrayIndex(index).map((child) => (
                  <Box key={child.key + '-' + child.arrayIndex}>
                    <RenderFields
                      field={child}
                      onChange={onChange}
                      onFilterChange={onFilterChange}
                      fieldNameOverride={
                        child.name == ARRAY_ITEM_NAME
                          ? childFieldNameOverride
                          : child.name
                      }
                      isEmptyAttributesVisible={
                        isFieldVisibilityOverridden(
                          field,
                          visibleFieldsOverride
                        )
                          ? true
                          : isEmptyAttributesVisible
                      }
                    />
                  </Box>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
};

export default ArrayInput;
